import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from './firebase';
import { signOut } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { useNavigate, Link } from 'react-router-dom';
import useAuth from './useAuth';
import useComputeTokensVisible from './useComputeTokensVisible';
import LinkedProvidersSection from './LinkedProvidersSection';
import SignInModal from './SignInModal';
import './App.css';

// Quota source of truth: Firestore config/quotas (hot-patchable).
// These are fallback defaults shown if the config doc fails to load.
const DEFAULT_QUOTAS = {
  storageMB:   { free: 25,  creator: 5120,  pro: 25600,  admin: null },
  itemLimit:   { free: 50,  creator: null,  pro: null,   admin: null },
  bandwidthGB: { free: 0.5, creator: 25,    pro: 50,     admin: null },
  priceCents:  { creator: 995, pro: 1995 },
  monthlyTokenGrant: { creator: 100, pro: 300 },
};
const TIER_LABELS = { free: 'Free', creator: 'Creator', pro: 'Pro', admin: 'Admin' };
const KNOWN_TIERS = new Set(['free', 'creator', 'pro', 'admin']);

function formatBytes(bytes) {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export default function AccountPage() {
  const { user, profile, loading } = useAuth();
  const computeTokensVisible = useComputeTokensVisible();
  const [tokenBalance, setTokenBalance] = useState(0);
  const [tokenLifetime, setTokenLifetime] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [planLoading, setPlanLoading] = useState(false);
  const [quotaConfig, setQuotaConfig] = useState(DEFAULT_QUOTAS);
  const [canonicalStorageBytes, setCanonicalStorageBytes] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const navigate = useNavigate();

  // Fallback return-to-game: a signed-out visitor can be sent to
  // /account?return=<url> (e.g. a leaderboard game opened directly, not iframed).
  // Once signed in, bounce them straight back. Same-origin only — never honor an
  // off-site return (open-redirect guard).
  const returnPath = useMemo(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('return');
      if (!raw) return '';
      const u = new URL(raw, window.location.origin);
      return u.origin === window.location.origin ? (u.pathname + u.search + u.hash) : '';
    } catch (_) { return ''; }
  }, []);
  useEffect(() => {
    if (returnPath && user && !user.isAnonymous) navigate(returnPath);
  }, [returnPath, user, navigate]);
  // Auto-open the sign-in box when we arrived here specifically to sign in.
  useEffect(() => {
    if (returnPath && !loading && (!user || user.isAnonymous)) setShowSignIn(true);
  }, [returnPath, loading, user]);

  // Subscribe to config/quotas (hot-patchable; merge with defaults so missing
  // keys don't crash the UI when an admin edits the doc by hand).
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'quotas'), (snap) => {
      if (snap.exists()) setQuotaConfig({ ...DEFAULT_QUOTAS, ...snap.data() });
    }, (err) => console.warn('[Account] quota config listen failed:', err.message));
    return () => unsub();
  }, []);

  // Subscribe to canonical storage usage doc — replaces the stale
  // profile.storageUsedBytes denormalized field that the old AccountPage read.
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid, 'usage', 'storage'), (snap) => {
      if (snap.exists()) setCanonicalStorageBytes(snap.data().totalBytes || 0);
      else setCanonicalStorageBytes(0);
    }, (err) => console.warn('[Account] storage usage listen failed:', err.message));
    return () => unsub();
  }, [user]);

  // Token state (only read when visible, but cheap enough to always subscribe).
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setTokenBalance(data.computeTokenBalance || 0);
        setTokenLifetime(data.computeTokenLifetime || 0);
      }
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user || user.isAnonymous || !computeTokensVisible) return;
    (async () => {
      const { collection: col, query: q, orderBy, limit, getDocs: gd } = await import('firebase/firestore');
      const txRef = col(db, 'users', user.uid, 'token_transactions');
      const snap = await gd(q(txRef, orderBy('createdAt', 'desc'), limit(20)));
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, [user, tokenBalance, computeTokensVisible]);

  // Published items owned by this user.
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    let cancelled = false;
    (async () => {
      setItemsLoading(true);
      try {
        const q = query(collection(db, 'endProducts'), where('publisherUid', '==', user.uid));
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map(d => ({ ...d.data(), _id: d.id }));
        list.sort((a, b) => (b.publishedAt?.seconds || 0) - (a.publishedAt?.seconds || 0));
        setItems(list);
      } catch (err) {
        console.warn('[Account] items fetch failed:', err.message);
      } finally {
        if (!cancelled) setItemsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const tierId = useMemo(() => {
    const raw = profile?.subscriptionTier || 'free';
    // Unknown/legacy tier strings (e.g. the pre-Phase-UP 'team' value) get
    // normalized to free so the upgrade button and quota lookups behave.
    const t = KNOWN_TIERS.has(raw) ? raw : 'free';
    const statusOK = (profile?.subscriptionStatus ?? 'active') === 'active';
    // Admin tier never lapses; paid tiers fall back to free if subscription lapsed.
    if (t === 'admin') return 'admin';
    return (t !== 'free' && statusOK) ? t : 'free';
  }, [profile]);

  // Effective quota: per-user override > tier default > free default.
  // null cap = unlimited (admin tier or admin-granted override).
  const effective = useMemo(() => {
    const pick = (key) => {
      const override = profile?.quotaOverride;
      if (override && Object.prototype.hasOwnProperty.call(override, key)) return override[key];
      const map = quotaConfig[key] || {};
      return Object.prototype.hasOwnProperty.call(map, tierId) ? map[tierId] : map.free;
    };
    return {
      storageMB:   pick('storageMB'),
      itemLimit:   pick('itemLimit'),
      bandwidthGB: pick('bandwidthGB'),
    };
  }, [quotaConfig, tierId, profile]);

  const tier = {
    label: TIER_LABELS[tierId] || tierId,
    storageMB: effective.storageMB,
    itemLimit: effective.itemLimit,
    private: tierId !== 'free',
  };

  // Prefer canonical users/{uid}/usage/storage (web-api maintains this on
  // every upload/publish/delete). Fall back to the legacy denormalized field
  // until the canonical listener has fetched once.
  const storageUsedBytes = canonicalStorageBytes ?? (profile?.storageUsedBytes || 0);
  const storageCapBytes = effective.storageMB == null ? null : effective.storageMB * 1024 * 1024;
  const storagePct = storageCapBytes == null
    ? 0
    : Math.min(100, Math.round((storageUsedBytes / storageCapBytes) * 100));

  const itemCount = items.length;
  const itemCapText = effective.itemLimit == null ? `${itemCount}/unlimited` : `${itemCount}/${effective.itemLimit}`;
  const itemPct = effective.itemLimit == null
    ? 0
    : Math.round((itemCount / effective.itemLimit) * 100);   // can exceed 100 — intentional, surfaces the overage
  // Worst-case dimension drives the banner above the plan card.
  const worstPct = Math.max(storagePct, itemPct);
  const usageState = worstPct >= 100 ? 'over' : worstPct >= 80 ? 'warning' : 'ok';
  const usageColor = (pct) => pct >= 100 ? '#dc2626' : (pct >= 80 ? '#f59e0b' : '#3b82f6');

  if (loading) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh', textAlign: 'center', padding: '120px 24px' }}>
        <p style={{ color: '#6b7280' }}>Loading…</p>
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh', textAlign: 'center', padding: '120px 24px' }}>
        <h2 style={{ color: '#111827', marginBottom: 12 }}>
          {returnPath ? 'Sign in to continue' : 'Sign in to view your account'}
        </h2>
        <p style={{ color: '#6b7280', marginBottom: 20 }}>
          {returnPath
            ? "Sign in and we'll take you right back to where you were."
            : 'Sign in with Google, Apple, or your email to manage your account, apps, and scores.'}
        </p>
        <button
          onClick={() => setShowSignIn(true)}
          style={{
            display: 'inline-block', padding: '10px 22px', borderRadius: 8, border: 'none',
            background: '#3b82f6', color: '#fff', fontSize: '0.9rem', fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Sign in
        </button>
        {showSignIn && <SignInModal onClose={() => setShowSignIn(false)} />}
      </div>
    );
  }

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  const handleUpgrade = async (planId = 'creator') => {
    setActionError(''); setPlanLoading(true);
    try {
      const createCheckout = httpsCallable(getFunctions(), 'createSubscriptionCheckout');
      const { data } = await createCheckout({ planId });
      window.location.href = data.url;
    } catch (err) {
      setActionError(err.message || 'Upgrade failed.');
    } finally {
      setPlanLoading(false);
    }
  };

  const handleManage = async () => {
    setActionError(''); setPlanLoading(true);
    try {
      const manage = httpsCallable(getFunctions(), 'manageSubscription');
      const { data } = await manage();
      window.location.href = data.url;
    } catch (err) {
      setActionError(err.message || "Couldn't open billing portal.");
    } finally {
      setPlanLoading(false);
    }
  };

  const handleVisibilityChange = async (item, nextVisibility) => {
    if (nextVisibility !== 'public' && tierId === 'free') {
      setActionError('Upgrade to Pro to make items unlisted or private.');
      return;
    }
    setActionError('');
    try {
      await updateDoc(doc(db, 'endProducts', item._id), { visibility: nextVisibility });
      setItems((prev) => prev.map((p) => p._id === item._id ? { ...p, visibility: nextVisibility } : p));
    } catch (err) {
      setActionError(err.message || 'Visibility change rejected.');
    }
  };

  // Phase HUB-SOFT (2026-05-17) — soft-delete from the hub. Flips
  // endProducts.removed; the URL /u/{uid}/{slug}/ continues to serve (true
  // soft delete: only the public hub listing hides). Restore via the same
  // toggle. No tier gate — soft delete is a basic right, not a Pro feature.
  const handleRemoveToggle = async (item) => {
    const willRemove = !item.removed;
    if (willRemove) {
      const ok = window.confirm(`Remove "${item.name || item.title || item.slug}" from the public Hub?\n\nThe URL will still work for you — only the Hub listing hides. You can restore it from this page.`);
      if (!ok) return;
    }
    setActionError('');
    try {
      await updateDoc(doc(db, 'endProducts', item._id), {
        removed: willRemove,
        removedAt: willRemove ? new Date() : null,
      });
      setItems((prev) => prev.map((p) => p._id === item._id ? { ...p, removed: willRemove } : p));
    } catch (err) {
      setActionError(err.message || (willRemove ? 'Remove failed.' : 'Restore failed.'));
    }
  };

  const card = {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
    padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };
  const labelStyle = {
    color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase',
    letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4,
  };

  const shortUid = (profile?.publisherShortUid) || (user.uid || '').slice(0, 8);

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh' }}>
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 80px' }}>

        {/* Profile header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', background: '#3b82f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4rem', color: '#fff', fontWeight: 700, flexShrink: 0,
          }}>
            {(profile?.displayName || user.email || 'U')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ color: '#111827', fontSize: '1.4rem', fontWeight: 700, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {profile?.displayName || 'mChatAI User'}
            </h1>
            <p style={{ color: '#6b7280', margin: '3px 0 0', fontSize: '0.85rem' }}>
              {user.email || 'Apple ID'}
              {shortUid && (
                <>
                  {' · '}
                  <Link to={`/user/${shortUid}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>View public profile →</Link>
                </>
              )}
            </p>
          </div>
          <button onClick={handleSignOut} style={{
            background: '#fff', border: '1px solid #d1d5db', color: '#374151',
            padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem',
            fontWeight: 500, flexShrink: 0,
          }}>Sign Out</button>
        </div>

        {/* Phase 1A.discoverability — editable display name. This is the
            handle that shows up on /leaderboards/<slug> when the user posts
            a score. Pre-populated from Firebase Auth (e.g. Google sign-in
            surfaces the user's Google profile name), but the user can
            override it here. Writes to /users/{uid}.displayName via
            Firestore — the submitScore Cloud Function reads from there. */}
        <DisplayNameEditor user={user} profile={profile} />

        {actionError && (
          <div style={{ ...card, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: '0.85rem' }}>
            {actionError}
          </div>
        )}

        {/* Phase Q6 — proactive cloud-usage banner. Renders above the plan
            card at ≥80% usage on any dimension. Admin/unlimited users never
            see it (usageState stays 'ok' when caps are null). */}
        {usageState !== 'ok' && (
          <div style={{
            ...card,
            borderColor: usageState === 'over' ? '#fecaca' : '#fde68a',
            background: usageState === 'over' ? '#fef2f2' : '#fffbeb',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
          }}>
            <div style={{
              fontSize: '1.4rem',
              color: usageState === 'over' ? '#dc2626' : '#d97706',
            }}>
              {usageState === 'over' ? '⛔' : '⚠️'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: usageState === 'over' ? '#991b1b' : '#92400e', fontWeight: 700, fontSize: '0.9rem' }}>
                {usageState === 'over'
                  ? 'Cloud limit reached — new publishes will be rejected'
                  : `Approaching cloud limit (${worstPct}%)`}
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: 2 }}>
                Storage {storagePct}% · Published items {itemPct}% · Tier: {tier.label}
              </div>
            </div>
            {tierId === 'free' && (
              <Link to="/pricing" style={{
                background: usageState === 'over' ? '#dc2626' : '#d97706',
                color: '#fff', border: 'none', padding: '8px 16px',
                borderRadius: 20, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                See plans
              </Link>
            )}
          </div>
        )}

        {/* Your plan */}
        <div style={{ ...card, borderColor: tierId === 'pro' ? '#c7d2fe' : tierId === 'creator' ? '#bae6fd' : '#e5e7eb', background: tierId === 'pro' ? '#eef2ff' : tierId === 'creator' ? '#f0f9ff' : '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={labelStyle}>Your Plan</div>
              <div style={{ color: '#111827', fontSize: '1.5rem', fontWeight: 700 }}>{tier.label}</div>
              {(tierId === 'pro' || tierId === 'creator') && profile?.subscriptionStatus && profile.subscriptionStatus !== 'active' && (
                <div style={{ color: '#b45309', fontSize: '0.8rem', marginTop: 2 }}>Status: {profile.subscriptionStatus}</div>
              )}
              {profile?.tierGrantedByAdmin === true && tierId !== 'admin' && (
                <div style={{
                  display: 'inline-block', marginTop: 6, padding: '2px 8px', borderRadius: 4,
                  background: '#fef3c7', color: '#92400e', fontSize: '0.7rem', fontWeight: 600,
                }}>
                  Simulated tier — admin-granted, not a paid subscription
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
              {tierId === 'free' && (
                <>
                  <button onClick={() => handleUpgrade('creator')} disabled={planLoading} style={{
                    background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 22px',
                    borderRadius: 24, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                    opacity: planLoading ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}>
                    {planLoading ? '…' : 'Creator · $9.95/mo'}
                  </button>
                  <button onClick={() => handleUpgrade('pro')} disabled={planLoading} style={{
                    background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe',
                    padding: '9px 20px', borderRadius: 24, fontWeight: 600, fontSize: '0.85rem',
                    cursor: 'pointer', opacity: planLoading ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}>
                    {planLoading ? '…' : 'Pro · $19.95/mo'}
                  </button>
                </>
              )}
              {tierId === 'creator' && (
                <>
                  <button onClick={() => handleUpgrade('pro')} disabled={planLoading} style={{
                    background: '#3b82f6', color: '#fff', border: 'none', padding: '9px 18px',
                    borderRadius: 20, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                    opacity: planLoading ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}>
                    Upgrade to Pro
                  </button>
                  <button onClick={handleManage} disabled={planLoading} style={{
                    background: '#fff', border: '1px solid #bae6fd', color: '#0369a1',
                    padding: '7px 16px', borderRadius: 20, fontWeight: 500, fontSize: '0.8rem',
                    cursor: 'pointer', opacity: planLoading ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}>
                    Manage subscription
                  </button>
                </>
              )}
              {tierId === 'pro' && (
                <button onClick={handleManage} disabled={planLoading} style={{
                  background: '#fff', border: '1px solid #c7d2fe', color: '#4338ca',
                  padding: '9px 18px', borderRadius: 20, fontWeight: 600, fontSize: '0.85rem',
                  cursor: 'pointer', opacity: planLoading ? 0.6 : 1,
                }}>
                  {planLoading ? '…' : 'Manage subscription'}
                </button>
              )}
              {tierId === 'admin' && (
                <div style={{
                  color: '#6b7280', fontSize: '0.8rem', fontStyle: 'italic',
                  padding: '9px 0',
                }}>
                  Internal account — no subscription to manage.
                </div>
              )}
            </div>
          </div>

          {/* Usage bars */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Storage</div>
              <div style={{ color: '#111827', fontSize: '0.9rem', fontWeight: 600, marginBottom: 4 }}>
                {formatBytes(storageUsedBytes)} of {effective.storageMB == null
                  ? 'unlimited'
                  : (effective.storageMB >= 1024 ? `${effective.storageMB / 1024} GB` : `${effective.storageMB} MB`)}
              </div>
              {effective.storageMB != null && (
                <div style={{ width: '100%', height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${storagePct}%`, height: '100%', background: usageColor(storagePct) }} />
                </div>
              )}
            </div>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Published items</div>
              <div style={{
                color: itemPct >= 100 ? '#dc2626' : (itemPct >= 80 ? '#92400e' : '#111827'),
                fontSize: '0.9rem',
                fontWeight: 600,
              }}>
                {itemCapText}
              </div>
              {effective.itemLimit != null && (
                <div style={{ width: '100%', height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginTop: 4 }}>
                  <div style={{ width: `${Math.min(100, itemPct)}%`, height: '100%', background: usageColor(itemPct) }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Your published work */}
        <h2 style={{ color: '#111827', fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>
          Your Published Work
        </h2>
        {itemsLoading ? (
          <div style={{ ...card, textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>Loading your items…</div>
        ) : items.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
            Nothing published yet. Build something in mChatAI+ and publish it to the Hub.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 28 }}>
            {items.map((p) => {
              const visibility = p.visibility || 'public';
              const title = p.name || p.title || p.slug || '(untitled)';
              const isRemoved = p.removed === true;
              return (
                <div key={p._id} style={{
                  ...card, marginBottom: 0, padding: 14, gap: 8,
                  display: 'flex', flexDirection: 'column',
                  // Soft-deleted items get a muted card so the user can SEE
                  // at a glance which entries they hid (vs ones they only
                  // toggled visibility on). Restore is one click away.
                  opacity: isRemoved ? 0.55 : 1,
                  borderColor: isRemoved ? '#fecaca' : '#e5e7eb',
                  background: isRemoved ? '#fef2f2' : '#fff',
                }}>
                  <div style={{ color: '#111827', fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {title}
                    {isRemoved && <span style={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 700, marginLeft: 6 }}>HIDDEN</span>}
                  </div>
                  <select
                    value={visibility}
                    onChange={(e) => handleVisibilityChange(p, e.target.value)}
                    disabled={isRemoved}
                    style={{
                      fontSize: '0.78rem', padding: '4px 8px', borderRadius: 6,
                      border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                    }}
                  >
                    <option value="public">Public</option>
                    <option value="unlisted" disabled={tierId === 'free'}>
                      Unlisted {tierId === 'free' ? '(Pro)' : ''}
                    </option>
                    <option value="private" disabled={tierId === 'free'}>
                      Private {tierId === 'free' ? '(Pro)' : ''}
                    </option>
                  </select>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Link to={`/hub/${p.slug || p._id}`} style={{ color: '#3b82f6', fontSize: '0.78rem', textDecoration: 'none' }}>
                      Open in Hub →
                    </Link>
                    <button
                      onClick={() => handleRemoveToggle(p)}
                      title={isRemoved ? 'Restore to the Hub' : 'Hide from the Hub (URL still works)'}
                      style={{
                        background: isRemoved ? '#16a34a' : 'transparent',
                        color: isRemoved ? '#fff' : '#b91c1c',
                        border: `1px solid ${isRemoved ? '#16a34a' : '#fecaca'}`,
                        padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
                        fontSize: '0.72rem', fontWeight: 600,
                      }}
                    >
                      {isRemoved ? 'Restore' : 'Remove'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Compute tokens (hidden behind flag) */}
        {computeTokensVisible && (
          <div style={{ ...card, borderColor: '#dbeafe', background: '#eff6ff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={labelStyle}>Compute Token Balance</div>
                <div style={{ color: '#111827', fontSize: '2.5rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {tokenBalance.toFixed(1)}
                </div>
                <div style={{ color: '#2563eb', fontSize: '0.85rem', marginTop: 4, fontWeight: 500 }}>
                  ${(tokenBalance * 0.01).toFixed(2)} USD value
                </div>
              </div>
              <button onClick={() => navigate('/cloud')} style={{
                background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 22px',
                borderRadius: 24, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(59,130,246,0.3)',
              }}>
                {tokenBalance > 0 ? 'Buy More' : 'Get Tokens'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 32, borderTop: '1px solid #bfdbfe', paddingTop: 16 }}>
              <div>
                <div style={{ ...labelStyle, color: '#3b82f6' }}>Lifetime purchased</div>
                <div style={{ color: '#1e40af', fontWeight: 700 }}>{tokenLifetime.toFixed(0)} tokens</div>
              </div>
              <div>
                <div style={{ ...labelStyle, color: '#3b82f6' }}>Lifetime spent</div>
                <div style={{ color: '#1e40af', fontWeight: 700 }}>{(tokenLifetime - tokenBalance).toFixed(0)} tokens</div>
              </div>
            </div>
          </div>
        )}

        <LinkedProvidersSection />

        <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#111827', fontSize: '0.9rem', fontWeight: 600 }}>Have another mChatAI account?</div>
            <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2 }}>
              Merge accounts to combine linked providers.
            </div>
          </div>
          <Link to="/account/merge" style={{
            background: '#fff', border: '1px solid #d1d5db', color: '#374151',
            padding: '8px 16px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600,
            textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 16,
          }}>
            Merge Accounts
          </Link>
        </div>

        <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#111827', fontSize: '0.9rem', fontWeight: 600 }}>Published Product Review Queue</div>
            <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: 2 }}>
              Review bugs and feature requests for products you own, then queue the valid ones for AI follow-up.
            </div>
          </div>
          <Link to="/account/review-queue" style={{
            background: '#111827', color: '#fff', border: 'none',
            padding: '8px 16px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 700,
            textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 16,
          }}>
            Open Queue
          </Link>
        </div>

        {/* Recent transactions (token-gated) */}
        {computeTokensVisible && transactions.length > 0 && (
          <>
            <h2 style={{ color: '#111827', fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>Recent Activity</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {transactions.map((tx) => (
                <div key={tx.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: '#fff', border: '1px solid #f3f4f6', borderRadius: 8, padding: '10px 16px',
                }}>
                  <div>
                    <span style={{ color: '#111827', fontSize: '0.85rem', fontWeight: 500 }}>
                      {tx.type === 'purchase' ? 'Token Purchase' : tx.model || 'API Call'}
                    </span>
                    {tx.provider && (
                      <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: 8 }}>{tx.provider}</span>
                    )}
                  </div>
                  <span style={{
                    color: tx.amount > 0 ? '#16a34a' : '#dc2626',
                    fontWeight: 700, fontSize: '0.85rem',
                  }}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// Phase 1A.discoverability — Display Name / Leaderboard Handle editor.
// Reads the user's current displayName from Firestore (or Firebase Auth as
// fallback for first-time visitors who haven't customized it). On save,
// writes to /users/{uid}.displayName via Firestore — the submitScore
// Cloud Function picks this up on the next score submission and it appears
// on /leaderboards/<slug> immediately. Existing scores keep the displayName
// they were posted with; that's the user's choice at submit time, snapshot.
function DisplayNameEditor({ user, profile }) {
  const initialName = profile?.displayName || user?.displayName || '';
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const dirty = name.trim() !== initialName.trim();
  const valid = name.trim().length >= 1 && name.trim().length <= 32;

  // Re-sync if the profile loads after mount.
  useEffect(() => {
    if (!dirty && initialName && name !== initialName) setName(initialName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialName]);

  const handleSave = async () => {
    if (!user || !valid || !dirty) return;
    setSaving(true);
    setSaveMsg('');
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: name.trim(),
      });
      setSaveMsg('Saved — your next score will use this name.');
      setTimeout(() => setSaveMsg(''), 4000);
    } catch (err) {
      setSaveMsg('Save failed: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{
      marginBottom: 28, padding: 14,
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
    }}>
      <h3 style={{ margin: '0 0 4px', color: '#111827', fontSize: '0.95rem', fontWeight: 700 }}>
        Leaderboard handle
      </h3>
      <p style={{ margin: '0 0 10px', color: '#6b7280', fontSize: '0.78rem' }}>
        The name shown on mchatai.com/leaderboards when you post a score.
        {!profile?.displayName && user?.displayName && (
          <> Defaults to your sign-in name (<strong>{user.displayName}</strong>) until you customize it.</>
        )}
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={32}
          placeholder="e.g. Lawrence"
          style={{
            flex: '1 1 220px', minWidth: 0,
            padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6,
            fontSize: '0.92rem', color: '#111827', background: '#fff',
          }}
        />
        <button
          onClick={handleSave}
          disabled={!valid || !dirty || saving}
          style={{
            padding: '8px 18px', borderRadius: 6, border: 'none',
            background: (!valid || !dirty || saving) ? '#d1d5db' : '#3b82f6',
            color: '#fff', fontWeight: 600, fontSize: '0.88rem',
            cursor: (!valid || !dirty || saving) ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {saveMsg && (
        <div style={{ marginTop: 8, fontSize: '0.8rem', color: saveMsg.startsWith('Saved') ? '#16a34a' : '#dc2626' }}>
          {saveMsg}
        </div>
      )}
    </section>
  );
}
